/**
 * Pre-logout task registry — BUG-146069.
 *
 * A surface that holds a SERVER-side resource for as long as it is open — the
 * steering-document editor holds an exclusive document lock — has to hand that
 * resource back when the user signs out. Otherwise the next person to open the
 * document sees "locked by another user" until the backend's idle sweep reaps
 * it, long after the holder has gone.
 *
 * The natural home for that would be an `auth:logout` listener. It cannot live
 * there, for two reasons that are easy to miss and both fail intermittently:
 *
 *   1. By the time `auth:logout` fires, `logout()` has already POSTed
 *      `/api/auth/logout` and run `clearAuthState()`. The cookies a release call
 *      needs are gone, so the call 401s instead of releasing anything.
 *   2. The shell's listener calls `window.location.replace('/login')`
 *      SYNCHRONOUSLY, so an async release started at that moment is aborted
 *      mid-flight by the navigation.
 *
 * The release therefore has to run BEFORE the logout request goes out.
 * `useAuthStore.logout` awaits `runPreLogoutTasks()` as its first step, giving
 * anything registered here a bounded window to release while credentials are
 * still valid.
 *
 * MODULE-FEDERATION CONTRACT — consumers MUST import from
 * `@helios/shared-domain/stores` (the barrel), never by a deep path to this
 * file. `@helios/shared-domain/stores` is an EXACT-match shared singleton in
 * every webpack config, so the barrel is the only request that resolves to one
 * shared instance across the shell and the remotes. A deep import is not
 * covered by that share key: the remote would get a private copy of this
 * registry, register into it, and the shell's `logout()` would run an empty
 * batch — the bug back, with nothing to show for it.
 */

/** Takes no arguments; any resolved value is ignored. */
export type PreLogoutTask = () => void | Promise<void>;

const tasks = new Set<PreLogoutTask>();

/**
 * Ceiling on the whole batch. A hung release must never trap the user in a
 * session they asked to leave, so sign-out proceeds regardless once this
 * elapses.
 */
export const PRE_LOGOUT_TASK_TIMEOUT_MS = 3000;

/**
 * Registers `task` to run before the next sign-out. Returns its unregister
 * function, so a React caller can return it straight from `useEffect`.
 */
export function registerPreLogoutTask(task: PreLogoutTask): () => void {
  tasks.add(task);
  return () => {
    tasks.delete(task);
  };
}

/**
 * Whether this tab holds anything that must be handed back before sign-out.
 *
 * Used by the cross-tab handshake (`preLogoutPeers`) to decide whether to answer
 * another tab's sign-out at all: a tab holding nothing stays silent, so the
 * common single-tab case is not delayed waiting for it.
 */
export function hasPreLogoutTasks(): boolean {
  return tasks.size > 0;
}

/**
 * What a batch actually did — BUG-146069-002.
 *
 * The batch used to return `Promise<void>`, so there was no channel to report on even if a
 * caller wanted to: a clean run, a run where every release threw, and a run cut short by the
 * ceiling all resolved with the same `undefined`. That is why the QA gate's accepted fallback
 * ("a frontend release that failed or exceeded its 3s ceiling" → the idle sweep) had no signal
 * saying it had been taken.
 *
 * `logout()` still ignores this — the values exist to be LOGGED, and to be carried across the
 * cross-tab handshake, where the answering tab is the only one that knows how its own release
 * went. Never a reason to block or fail sign-out.
 */
export interface PreLogoutBatchResult {
  /** Tasks registered when the batch started. */
  total: number;
  /** Tasks that finished, successfully or not, before the ceiling. */
  settled: number;
  /** Tasks that threw or rejected — each of those released nothing. */
  failed: number;
  /** True when the ceiling won with work still outstanding. */
  timedOut: boolean;
}

/**
 * Runs every registered task concurrently, resolving when they have all settled
 * or `timeoutMs` has elapsed — whichever comes first.
 *
 * Never rejects, and one task's failure never cancels its siblings. Logout must
 * not fail because a release failed: the user asked to sign out, and the
 * backend's idle sweep remains the backstop for anything not handed back.
 *
 * Reports what happened via {@link PreLogoutBatchResult} and a console line (BUG-146069-002),
 * having previously done neither.
 */
export async function runPreLogoutTasks(
  timeoutMs: number = PRE_LOGOUT_TASK_TIMEOUT_MS
): Promise<PreLogoutBatchResult> {
  if (tasks.size === 0) return { total: 0, settled: 0, failed: 0, timedOut: false };

  // Snapshot before awaiting: a task may unregister itself (its component
  // unmounts) while the batch is in flight, which would mutate the live Set.
  const snapshot = Array.from(tasks);
  let settled = 0;
  let failed = 0;

  const pending = snapshot.map(async (task) => {
    try {
      await task();
    } catch (error) {
      // Still swallowed — logout must not fail because a release failed. But no longer SILENT
      // (BUG-146069-002): a task that threw released nothing, and whoever investigates a recurring
      // abandoned lock needs to know that happened.
      failed += 1;
      console.error('[BUG-146069] a pre-logout release task threw and released nothing:', error);
    } finally {
      settled += 1;
    }
  });

  // Labelled, because an unlabelled `race` cannot say WHICH promise won — the second half of
  // what made this undiagnosable, since the ceiling and a clean finish both resolved `undefined`.
  const all = Promise.all(pending).then(() => 'completed' as const);
  const timeout = new Promise<'timedOut'>((resolve) => {
    setTimeout(() => resolve('timedOut'), timeoutMs);
  });

  const timedOut = (await Promise.race([all, timeout])) === 'timedOut';
  const result: PreLogoutBatchResult = { total: snapshot.length, settled, failed, timedOut };

  if (timedOut) {
    console.warn(
      `[BUG-146069] pre-logout releases hit the ${timeoutMs}ms ceiling with ` +
        `${result.total - result.settled} of ${result.total} still outstanding — sign-out ` +
        'proceeded and those resources may not have been handed back.'
    );
  } else if (failed > 0) {
    console.warn(
      `[BUG-146069] pre-logout batch finished with ${failed} failed release(s) of ${result.total} — ` +
        'see the errors above.'
    );
  }

  return result;
}

/** Test seam: drops every registration so suites cannot leak tasks into each other. */
export function clearPreLogoutTasks(): void {
  tasks.clear();
}
